import type { Level, LevelPair, Pos } from '@color-dots/game';

export type WordMode = 'translation' | 'category';

export interface WordEntry {
  id: string;
  source: string;
  target: string;
  category: string;
  difficulty: number;
  deprecated?: boolean;
}

export interface WordVocabulary {
  topicId: string;
  version: number;
  localeFrom: string;
  localeTo: string;
  words: WordEntry[];
}

export interface WordLevelPair {
  pairId: string;
  color: string;
  a: Pos;
  b: Pos;
  source: string;
  target: string;
}

export interface WordLevelDef {
  id: string;
  size: number;
  mode: WordMode;
  pairs: WordLevelPair[];
  decoys: string[];
}

export interface WordLevelsPack {
  topicId: string;
  version: number;
  mode: WordMode;
  levels: WordLevelDef[];
}

export interface WordTopic {
  id: string;
  title: { ru: string; en: string };
  icon: string;
  vocabularyFile?: string;
  levelsFile?: string;
  levelCount: number;
  status: 'available' | 'coming-soon';
}

export interface WordManifest {
  version: number;
  topics: WordTopic[];
}

export interface WordLabel {
  text: string;
  role: 'source' | 'target';
}

export interface WordLevelMeta {
  def: WordLevelDef;
  level: Level;
  /** pos key "x,y" -> label */
  labels: Map<string, WordLabel>;
  /** color -> pairId */
  pairIds: Map<string, string>;
}

export function wordLevelToGameLevel(def: WordLevelDef): WordLevelMeta {
  const pairs: LevelPair[] = def.pairs.map((p) => ({
    color: p.color,
    a: p.a,
    b: p.b,
  }));

  const labels = new Map<string, WordLabel>();
  const pairIds = new Map<string, string>();

  for (const p of def.pairs) {
    const [ax, ay] = p.a;
    const [bx, by] = p.b;
    labels.set(`${ax},${ay}`, { text: p.source, role: 'source' });
    labels.set(`${bx},${by}`, { text: p.target, role: 'target' });
    pairIds.set(p.color, p.pairId);
  }

  for (const decoy of def.decoys) {
    // decoys handled separately when present
    void decoy;
  }

  return {
    def,
    level: {
      id: def.id,
      size: def.size,
      difficulty: 'easy',
      pairs,
      meta: { author: 'word-connect', created: '2026-06-07' },
    },
    labels,
    pairIds,
  };
}
